import {
  TABLET_BREAKPOINT,
  tokens,
  Tooltip,
  Typography,
  Button,
  SvgIcon,
  SvgNames,
  MOBILE_BREAKPOINT,
} from "@sunrun/experience-ui-components";
import React from "react";
import styled from "@emotion/styled/macro";
import { useMediaQuery } from "react-responsive";
import sunrunMobile from "../../assets/sunrunMobile.svg";
import DownloadAppQR from "../../assets/DownloadAppQR.svg";
import AppleAppStore from "../../assets/AppleAppStore.svg";
import GooglePlayStore from "../../assets/GooglePlayStore.svg";
import NewAppleAppStore from "../../assets/NewAppleAppStore.svg";
import NewGooglePlayStore from "../../assets/NewGooglePlayStore.svg";
import fullIPhoneAutopayImg from "../../assets/iphone-autopay-tablet-view.svg";
import halfIPhoneAutopayImg from "../../assets/iphone-autopay-fullscreen-view.svg";
import iphoneTabletView from "../../assets/iphone-tablet-view.svg";
import iphone from "../../assets/iPhone.png";
import newHomesIphoneImage from "../../assets/new-homes-iphone.svg";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";

const STEP_TOOLTIPS = [
  "Review your Sunrun offer/s and choose the best option to move forward with.",
  "We’ll qualify your home to ensure a solar system can be safely installed.",
  "Based on your home’s unique qualities, we’ll create a custom design.",
  "Once your design is final, we’ll submit it to the city for permitting.",
  "With help from your utility and local AHJ, we’ll complete any pre-installation work necessary.",
  "We’ll install your system once we have permits and all other pre-installation approvals.",
  "Your System will be inspected by the city/country and utility",
  "Once we have the utility’s approval, we’’ll flip the switch and you can enjoy the benefits of going solar!",
] as const;

type PageContext = "Offer Review" | "CAP";
type Props = {
  pageContext: PageContext;
  layout?: "classic" | "new";
  isNewHomes?: boolean;
  isCustomer?: boolean;
  /** New layout only: clip the phone at the banner's bottom edge instead of letting it overhang. */
  clipPhone?: boolean;
};

type LayoutBannerProps = {
  pageContext: PageContext;
  isMobile: boolean;
  isTablet: boolean;
  isNewHomes?: boolean;
  isCustomer?: boolean;
  clipPhone?: boolean;
};

const ClassicMobileAppBanner = ({
  pageContext,
  isMobile,
  isTablet,
}: LayoutBannerProps) => {
  const iphoneImageAutopay =
    isTablet && !isMobile ? fullIPhoneAutopayImg : halfIPhoneAutopayImg;
  const iphoneImage = isTablet && !isMobile ? iphoneTabletView : iphone;

  return (
    <MobileAppContent isTablet={isTablet} isMobile={isMobile} layout="classic">
      <DownloadInfo isTablet={isTablet} isMobile={isMobile} layout="classic">
        <TopRow>
          <img src={sunrunMobile} alt={"sunrun mobile app"} />
          <Typography
            size={isMobile ? 24 : isTablet ? 20 : 32}
            color={tokens.WHITE}
            fontWeight="medium"
            lineHeight={isTablet ? 24 : 44}
          >
            Install the Sunrun mobile app to track your progress
          </Typography>
        </TopRow>
        <Typography
          color={tokens.WHITE}
          size={isMobile ? 18 : isTablet ? 16 : 22}
        >
          Newly designed and easy-to-use, our app is the single access point for
          you to monitor your system, track energy production, manage your
          account, and get support.
        </Typography>
        <Typography
          size={20}
          fontWeight={"medium"}
          color={tokens.WHITE}
          style={{ textAlign: "center" }}
        >
          Download it today.
        </Typography>
        <ImgRow isTablet={isTablet}>
          {isTablet && (
            <StyledImg
              isTablet={isTablet}
              src={DownloadAppQR}
              alt={"QR code to download Sunrun app"}
            />
          )}
          <MobileStoreLink
            target="_blank"
            rel="noreferrer noopener"
            href="https://apps.apple.com/us/app/sunrun/id6443806453"
            aria-label="Apple App store"
          >
            <img src={AppleAppStore} alt={"Apple App Store logo"} />
          </MobileStoreLink>
          {!isTablet && (
            <StyledImg
              isTablet={isTablet}
              src={DownloadAppQR}
              alt={"QR code to download Sunrun app"}
            />
          )}
          <MobileStoreLink
            target="_blank"
            rel="noreferrer noopener"
            href="https://play.google.com/store/apps/details?id=com.sunrun.customerapp"
            aria-label="Google Play store"
          >
            <img src={GooglePlayStore} alt={"Google Play store logo"} />
          </MobileStoreLink>
        </ImgRow>
      </DownloadInfo>
      <PhoneContainer isTablet={isTablet} isMobile={isMobile}>
        <img
          src={
            pageContext === "Offer Review" ? iphoneImage : iphoneImageAutopay
          }
          alt={"iPhone with autopay enroll form"}
        />
      </PhoneContainer>
    </MobileAppContent>
  );
};

const NewMobileAppBanner = ({
  isMobile,
  isTablet,
  isNewHomes,
  isCustomer,
  clipPhone,
}: Omit<LayoutBannerProps, "pageContext">) => {
  const newIphoneImage = iphoneTabletView;

  return (
    <NewMobileAppContent
      data-testid="mobile-app-download-banner-new"
      isTablet={isTablet}
      isMobile={isMobile}
      clipPhone={clipPhone}
    >
      <NewDownloadInfo isTablet={isTablet} isMobile={isMobile}>
        <TextRow>
          <AppDownloadTitle color={tokens.WHITE} size={44}>
            Stay up-to-date on your solar progress
          </AppDownloadTitle>
          <AppDownloadDescription
            color={tokens.WHITE}
            size={20}
            fontWeight="light"
          >
            Download the Sunrun app to track your progress and a whole lot more
          </AppDownloadDescription>
        </TextRow>
        <IconRow isMobile={isMobile}>
          <IconImg src={sunrunMobile} alt={"sunrun mobile app"} />
          <IconImg src={DownloadAppQR} alt={"QR code to download Sunrun app"} />
        </IconRow>
        <LinkRow isMobile={isMobile}>
          <MobileStoreLink
            target="_blank"
            rel="noreferrer noopener"
            href="https://apps.apple.com/us/app/sunrun/id6443806453"
            aria-label="Apple App store"
          >
            <img src={NewAppleAppStore} alt={"Apple App Store logo"} />
          </MobileStoreLink>
          <MobileStoreLink
            target="_blank"
            rel="noreferrer noopener"
            href="https://play.google.com/store/apps/details?id=com.sunrun.customerapp"
            aria-label="Google Play store"
          >
            <img src={NewGooglePlayStore} alt={"Google Play store logo"} />
          </MobileStoreLink>
        </LinkRow>
        {!isCustomer && (
          <TextRow>
            <DemoTip>
              <Typography size={16} color={tokens.BRAND_HEROBLUE}>
                Demo the Sunrun Customer App now!
              </Typography>
              <StyledButton
                color="link"
                onClick={() => {
                  window.open(
                    "https://current.sunrun.com/site/7b6848bd-8bfb-5e2e-b56d-41b94260487d/page/c0b98bac-6808-54d5-97c3-46d39d3769b9",
                    "_blank"
                  );
                }}
              >
                View tutorial
              </StyledButton>
            </DemoTip>
          </TextRow>
        )}
      </NewDownloadInfo>
      <NewPhoneContainer isTablet={isTablet} isMobile={isMobile}>
        <img
          src={isNewHomes ? newHomesIphoneImage : newIphoneImage}
          alt={"iPhone with opened app"}
        />
        {!isNewHomes && (
          <StepTooltipWrapper>
            {STEP_TOOLTIPS.map((content) => (
              <StyledTooltip key={content} content={content}>
                <StepInfoTrigger type="button" aria-label="Step information">
                  <SvgIcon
                    name={SvgNames.InfoCircle}
                    width={14}
                    height={14}
                    color={tokens.HEROBLUE_10}
                    testId="step-info-icon"
                  />
                </StepInfoTrigger>
              </StyledTooltip>
            ))}
          </StepTooltipWrapper>
        )}
      </NewPhoneContainer>
    </NewMobileAppContent>
  );
};

const MobileAppDownloadBanner = ({
  pageContext,
  layout = "classic",
  isNewHomes,
  isCustomer,
  clipPhone,
}: Props) => {
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  return layout === "new" ? (
    <NewMobileAppBanner
      isMobile={isMobile}
      isTablet={isTablet}
      isNewHomes={isNewHomes}
      isCustomer={isCustomer}
      clipPhone={clipPhone}
    />
  ) : (
    <ClassicMobileAppBanner
      pageContext={pageContext}
      isMobile={isMobile}
      isTablet={isTablet}
    />
  );
};

const MobileAppContent = styled.section<{
  isTablet: boolean;
  isMobile: boolean;
  layout: "classic" | "new";
}>`
  background: linear-gradient(180deg, #1f2647 0%, black 100%);
  display: flex;
  flex-direction: ${({ isMobile, layout }) =>
    isMobile ? "column" : layout === "classic" ? "row" : "row-reverse"};
  padding: ${({ isMobile, isTablet }) =>
    isMobile ? "24px 24px 0" : isTablet ? "24px" : "80px 80px 0"};
  gap: ${({ isMobile, isTablet }) =>
    isMobile ? "32px" : isTablet ? "70px" : "100px"};
`;

const DownloadInfo = styled.div<{
  isTablet: boolean;
  isMobile: boolean;
  layout: "classic" | "new";
}>`
  width: ${({ isMobile }) => (isMobile ? "unset" : "55%")};
  display: flex;
  flex-direction: column;
  gap: ${({ layout }) => (layout === "classic" ? "24px" : "40px")};
  padding-right: ${({ isMobile, isTablet }) =>
    !isMobile && isTablet ? "60px" : "0"};
  padding-bottom: ${({ isMobile, isTablet }) =>
    !isMobile && isTablet ? "60px" : "0"};
  box-sizing: ${({ isTablet }) => (isTablet ? "border-box" : "unset")};

  & > img {
    height: 64px;
    width: 64px;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-direction: row;
`;

const ImgRow = styled.div<{ isTablet: boolean }>`
  display: flex;
  gap: ${({ isTablet }) => (isTablet ? "16px" : "13%")};
  flex-direction: ${({ isTablet }) => (isTablet ? "column" : "row")};
  align-items: ${({ isTablet }) => (isTablet ? "center" : "unset")};
  justify-content: space-between;
`;

const StyledImg = styled.img<{ isTablet: boolean }>`
  width: ${({ isTablet }) => (isTablet ? "90px" : "110px")};
`;

const PhoneContainer = styled.div<{ isTablet: boolean; isMobile: boolean }>`
  display: flex;
  justify-content: center;

  & > img {
    max-width: ${({ isMobile, isTablet }) =>
      !isMobile && isTablet ? "none" : "310px"};
    height: auto;
    object-fit: cover;
  }
`;

const MobileStoreLink = styled.a`
  display: flex;
  align-items: center;
`;

const NewMobileAppContent = styled.section<{
  isTablet: boolean;
  isMobile: boolean;
  clipPhone?: boolean;
}>`
  background: linear-gradient(180deg, #1f2647 0%, black 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: ${({ isMobile }) => (isMobile ? "column" : "row-reverse")};
  padding: ${({ isMobile, isTablet }) =>
    isMobile ? "16px 16px 0" : "0px 48px 0"};
  gap: ${({ isMobile, isTablet }) =>
    isMobile ? "50px" : isTablet ? "70px" : "100px"};
  position: relative;
  margin-bottom: ${({ isMobile, clipPhone }) =>
    clipPhone ? "0" : isMobile ? "48px" : "50px"};
  overflow: ${({ clipPhone }) => (clipPhone ? "hidden" : "visible")};
`;

const NewDownloadInfo = styled.div<{ isTablet: boolean; isMobile: boolean }>`
  width: fit-content;
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? "32px" : "40px")};
  box-sizing: ${({ isTablet }) => (isTablet ? "border-box" : "unset")};

  & > img {
    height: 64px;
    width: 64px;
  }
`;

const TextRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 380px;
`;

const IconRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: ${({ isMobile }) => (isMobile ? "center" : "unset")};
`;

const IconImg = styled.img`
  width: 95px;
  height: 95px;
`;

const LinkRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: 16px;
  justify-content: ${({ isMobile }) => (isMobile ? "center" : "unset")};
`;

const AppDownloadTitle = styled(Typography)`
  line-height: 120% !important;
  letter-spacing: -0.88px;
`;

const AppDownloadDescription = styled(Typography)`
  line-height: 110% !important;
  letter-spacing: -0.4px;
`;

const NewPhoneContainer = styled.div<{ isTablet: boolean; isMobile: boolean }>`
  display: flex;
  justify-content: center;
  position: relative;
  left: 0;
  width: ${({ isMobile, isTablet }) =>
    isMobile || isTablet ? "300px" : "320px"};
  height: ${({ isMobile, isTablet }) =>
    isMobile || isTablet ? "606px" : "648px"};
  bottom: ${({ isMobile }) => (isMobile ? "-48px" : "-50px")};

  & > img {
    object-fit: cover;
  }
`;

const StepTooltipWrapper = styled.div`
  position: absolute;
  top: 149px;
  left: 30px;
  width: 254px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    top: 139px;
    left: 28px;
    width: 235px;
    gap: 8px;
  }
`;

const StyledTooltip = styled(Tooltip)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: end;
`;

const StepInfoTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 48px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:hover svg {
    opacity: 0.9;
  }

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    height: 46px;
  }
`;

const DemoTip = styled.div`
  background: ${tokens.BRAND_CHARGEBLUE};
  border-radius: 8px;
  padding: 16px;
  text-align: left;
  width: fit-content;
  max-width: 100%;

  p {
    line-height: 110%;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-self: center;
  }
`;

const StyledButton = styled(Button)`
  color: ${tokens.BRAND_HEROBLUE};
  border: none;
  text-decoration: underline;
`;

export { MobileAppDownloadBanner };
